import {captureException} from '@sentry/browser';
import {closeDialog, openDialog} from 'platform/components';

import {ReactNode, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';

import {isNil} from 'ramda';

import {APP_UPDATE_SIMULATION_EVENT, browserStorageKey} from '@dms/config';
import i18n from '@dms/i18n';

import {getModuleNameFromPathname} from '../../../utils/getModuleNameFromPathname';
import {ApplicationUpdateProvider} from '../contexts/ApplicationUpdateContext';
import {useUpdateApp} from '../hooks/useUpdateApp';
import {AppUpdateKind, getAppUpdateKind} from '../utils/getAppUpdateKind';
import {UpdateApplicationDialog} from './UpdateApplicationDialog';

type ApplicationVersionResponse = {
  version: string;
  pipeline: string;
  job: string;
  ref?: string;
};

interface ApplicationUpdateBoundaryProps {
  children: ReactNode;
}

const UPDATE_AVAILABLE_DIALOG = 'UPDATE_AVAILABLE_DIALOG';
const DEV_ENVIRONMENT = 'dev';
const SIMULATED_VERSION_SUFFIX = '-simulated';

function readAppUpdateSimulation() {
  const simulationJson = sessionStorage.getItem(browserStorageKey.APP_UPDATE_SIMULATION);
  if (isNil(simulationJson)) {
    return undefined;
  }
  sessionStorage.removeItem(browserStorageKey.APP_UPDATE_SIMULATION);
  try {
    const simulation: unknown = JSON.parse(simulationJson);
    if (
      !isNil(simulation) &&
      typeof simulation === 'object' &&
      'runningRef' in simulation &&
      typeof simulation.runningRef === 'string' &&
      'deployedRef' in simulation &&
      typeof simulation.deployedRef === 'string'
    ) {
      return {runningRef: simulation.runningRef, deployedRef: simulation.deployedRef};
    }
  } catch {
    // Ignore invalid one-shot simulation data.
  }
  return undefined;
}

export function ApplicationUpdateBoundary(props: ApplicationUpdateBoundaryProps) {
  const location = useLocation();
  const [isPatchUpdateAvailable, setIsPatchUpdateAvailable] = useState(false);
  const isUpdatePostponed = useRef(false);
  const postponedModuleName = useRef<string | undefined>(undefined);

  const updateApp = useUpdateApp();

  useEffect(
    function applyPostponedUpdateOnModuleChange() {
      if (!isUpdatePostponed.current) {
        return;
      }

      const currentModuleName = getModuleNameFromPathname(location.pathname);
      if (postponedModuleName.current === currentModuleName) {
        return;
      }

      updateApp();
    },
    [updateApp, location]
  );

  useEffect(
    function pollForAppUpdate() {
      sessionStorage.removeItem(browserStorageKey.CURRENT_VERSION);

      const checkForAppUpdate = () =>
        fetch('/version.json', {cache: 'no-store'})
          .then((response) => response.json() as Promise<ApplicationVersionResponse>)
          .then((data) => {
            try {
              if (isNil(sessionStorage.getItem(browserStorageKey.CURRENT_VERSION))) {
                sessionStorage.setItem(browserStorageKey.CURRENT_VERSION, data.version);
              }
              let runningRef = process.env.RELEASE_REF;
              const simulation =
                process.env.ENV_TYPE === DEV_ENVIRONMENT ? readAppUpdateSimulation() : undefined;
              if (!isNil(simulation)) {
                runningRef = simulation.runningRef;
                data = {
                  ...data,
                  version: `${data.version}${SIMULATED_VERSION_SUFFIX}`,
                  ref: simulation.deployedRef,
                };
              }
              if (sessionStorage.getItem(browserStorageKey.CURRENT_VERSION) !== data.version) {
                const updateKind: AppUpdateKind = getAppUpdateKind(runningRef, data.ref);
                if (updateKind === 'patch') {
                  setIsPatchUpdateAvailable(true);
                  return;
                }

                const handleCloseDialog = () => {
                  isUpdatePostponed.current = true;
                  postponedModuleName.current = getModuleNameFromPathname(location.pathname);
                  closeDialog(UPDATE_AVAILABLE_DIALOG);
                };
                clearInterval(intervalId);
                openDialog(
                  <UpdateApplicationDialog onPostpone={handleCloseDialog} onUpdate={updateApp} />,
                  {
                    id: UPDATE_AVAILABLE_DIALOG,
                    title: i18n.t('general.checkApplicationVersion.updateAvailableTitle'),
                    onCloseComplete: handleCloseDialog,
                  }
                );
              }
            } catch (e) {
              captureException(e);
              window.location.reload();
            }
          })
          .catch(() => window.location.reload());

      const intervalId = setInterval(checkForAppUpdate, CHECK_FOR_APP_UPDATE_INTERVAL_MS);

      if (process.env.ENV_TYPE === DEV_ENVIRONMENT) {
        window.addEventListener(APP_UPDATE_SIMULATION_EVENT, checkForAppUpdate);
      }

      return () => {
        clearInterval(intervalId);
        window.removeEventListener(APP_UPDATE_SIMULATION_EVENT, checkForAppUpdate);
      };
    },
    [updateApp, location]
  );

  return (
    <ApplicationUpdateProvider value={{isPatchUpdateAvailable, updateApp}}>
      {props.children}
    </ApplicationUpdateProvider>
  );
}

const CHECK_FOR_APP_UPDATE_INTERVAL_MS = 300_000;
